import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from "@reduxjs/toolkit";

import { FormState } from "../../components/appData.slice";

export type UIPayload = {
  field: string;
  value: string | boolean;
};
export type UIObjectPayload = {
  field: string;
  value: Record<string, string>;
};
export type UIState = Record<
  string,
  | string
  | boolean
  | Record<string, unknown>
  | { label: string; value: string }[]
>;
type UIObjectState = Record<string, Record<string, string>>;

export type GenericState<T, T2 = UIState> = {
  data: T;
  ui: T2;
};

declare type NoInfer<T> = [T][T extends any ? 0 : never];

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createGenericUISlice = <
  T,
  Reducers extends SliceCaseReducers<GenericState<T>>
>({
  name = "",
  initialState,
  reducers,
  extraReducers,
}: {
  name: string;
  initialState: GenericState<T>;
  reducers: ValidateSliceCaseReducers<GenericState<T>, Reducers>;
  extraReducers?: (
    builder: ActionReducerMapBuilder<NoInfer<GenericState<T>>>
  ) => void;
}) =>
  createSlice({
    name,
    initialState,
    reducers: {
      setUIField(state: GenericState<T>, action: PayloadAction<UIPayload>) {
        const { field, value } = action.payload;

        state.ui[field] = value;
      },

      setUIObjectField(
        state: GenericState<T, UIObjectState>,
        action: PayloadAction<UIObjectPayload>
      ) {
        const { field, value } = action.payload;

        if (!state.ui[field]) {
          state.ui[field] = value;
        }

        const statePayload: Record<string, string> = {};
        for (const [key, valueFromState] of Object.entries(state.ui[field])) {
          statePayload[key] = valueFromState;
        }

        state.ui[field] = { ...statePayload, ...value };
      },
      ...reducers,
    },
    extraReducers,
  });
